import { useState, ReactNode } from 'react'
import { HiOutlineChartBar, HiOutlineChartPie } from 'react-icons/hi2'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

export type ChartDatum = {
  label: string
  value: number
  /** Optional — if omitted, cycles through the default palette */
  color?: string
}

type Props = {
  title: string
  icon: ReactNode
  data: ChartDatum[]
  /** Initial chart type. Defaults to 'pie'. */
  defaultChart?: 'pie' | 'bar'
}

// Fallback palette for questions that don't supply per-option colors. Ordered
// to roughly match the tones used in the Figma analytics mockup (pink, lime,
// purple, teal, orange, blue, magenta).
const DEFAULT_PALETTE = [
  '#E6007E',
  '#93D500',
  '#7C3AED',
  '#00B8A9',
  '#F59E0B',
  '#2563EB',
  '#DB2777',
  '#0EA5E9',
]

function resolveColor(datum: ChartDatum, index: number): string {
  return datum.color ?? DEFAULT_PALETTE[index % DEFAULT_PALETTE.length]
}

export default function ChartCard({ title, icon, data, defaultChart = 'pie' }: Props) {
  const [chart, setChart] = useState<'pie' | 'bar'>(defaultChart)

  const hasData = data.some((d) => d.value > 0)
  const colored = data.map((d, i) => ({ ...d, color: resolveColor(d, i) }))

  return (
    <div className="flex flex-col overflow-hidden rounded-xl bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between gap-2 border-b border-[#f5f5f5] px-5 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-ink" aria-hidden="true">
            {icon}
          </span>
          <h3 className="truncate font-display text-lg font-medium tracking-tight text-ink">
            {title}
          </h3>
        </div>
        <ChartToggle value={chart} onChange={setChart} />
      </div>

      <div className="flex flex-col gap-3 px-5 pb-5 pt-4">
        {hasData ? (
          <>
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                {chart === 'pie' ? (
                  <PieChart>
                    <Pie
                      data={colored}
                      dataKey="value"
                      nameKey="label"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      stroke="none"
                      label={({ percent }) =>
                        percent && percent > 0.03 ? `${Math.round(percent * 100)}%` : ''
                      }
                      labelLine={false}
                    >
                      {colored.map((d) => (
                        <Cell key={d.label} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  </PieChart>
                ) : (
                  <BarChart data={colored} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eeeeee" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={{ stroke: '#e5e5e5' }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={{ stroke: '#e5e5e5' }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {colored.map((d) => (
                        <Cell key={d.label} fill={d.color} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>

            <ul className="flex flex-col gap-1.5">
              {colored.map((d) => (
                <li
                  key={d.label}
                  className="flex items-center gap-2 font-display text-xs text-ink"
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: d.color }}
                    aria-hidden="true"
                  />
                  <span className="truncate">
                    {d.label}: {d.value}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="flex h-[220px] items-center justify-center font-display text-sm text-gray-400">
            No responses yet
          </div>
        )}
      </div>
    </div>
  )
}

function ChartToggle({
  value,
  onChange,
}: {
  value: 'pie' | 'bar'
  onChange: (v: 'pie' | 'bar') => void
}) {
  return (
    <div className="flex items-center rounded-full bg-[#f5f5f5] p-0.5">
      <button
        type="button"
        onClick={() => onChange('pie')}
        aria-label="Show as pie chart"
        aria-pressed={value === 'pie'}
        className={`flex h-6 w-7 items-center justify-center rounded-full transition ${
          value === 'pie' ? 'bg-white text-ink shadow-sm' : 'text-gray-500 hover:text-ink'
        }`}
      >
        <HiOutlineChartPie className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange('bar')}
        aria-label="Show as bar chart"
        aria-pressed={value === 'bar'}
        className={`flex h-6 w-7 items-center justify-center rounded-full transition ${
          value === 'bar' ? 'bg-white text-ink shadow-sm' : 'text-gray-500 hover:text-ink'
        }`}
      >
        <HiOutlineChartBar className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
